import GavelIcon from '@mui/icons-material/Gavel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Badge, Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { type ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';

import { bucketLength, useAnalytics } from '~/analytics';
import { UserContext } from '~/auth/userContext';
import { Notes } from '~/components/common/Notes';
import { tabLabelWithCount } from '~/components/common/TabCount';
import { TabPanel, useTabs } from '~/components/common/tabs';
import { TabContext, TabStrip } from '~/components/common/tabs/useTabs';
import { DisputeAgentPanel, useDisputeAgent } from '~/components/disputes';
import { selectedInvoiceIdKey, selectedTabSearchParam } from '~/components/global/constants';
import Attachments, { countLinkedAttachments } from '~/components/invoices/Attachments';
import { CarsServicedSection } from '~/components/invoices/detailPage/related/CarsServicedSection';
import { ContractsSection } from '~/components/invoices/detailPage/related/ContractsSection';
import { InvoicesSection } from '~/components/invoices/detailPage/related/InvoicesSection';
import { TicketsSection } from '~/components/invoices/detailPage/related/TicketsSection';
import { RelatedPanel } from '~/components/invoices/detailPage/RelatedPanel';
import { useRelatedCounts } from '~/components/invoices/detailPage/useRelatedCounts';
import { useHostedInWorkQueueDialog } from '~/components/workQueue/dialogHost';
import useInvoice from '~/data/hooks/useInvoice';
import type { Invoice } from '~/data/schemas/invoice';
import { InvoiceStatus, isCredit } from '~/data/schemas/invoice';
import type { Ticket } from '~/data/schemas/ticket';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { BUTTON_SX, pageBandColor, SURFACE_RADIUS } from '~/theme';
import { useFeatureFlags, useFlagsReady } from '~/utils/featureFlags';

export enum Tab {
  CarsServiced = 'serviced',
  Tickets = 'tickets',
  RelatedInvoices = 'invoices & proposals',
  Contract = 'contract',
  Notes = 'notes',
  Attachments = 'attachments',
  Dispute = 'dispute',
}

/**
 * The relational tabs, in order. What else exists for the elevators an invoice serviced
 * sits on Cars Serviced rather than doubling the Tickets and Invoices tabs, which hold
 * what is linked to this invoice.
 */
const RELATED_TABS = [Tab.Tickets, Tab.RelatedInvoices, Tab.CarsServiced, Tab.Contract] as const;

const DAYS_SINCE_LAST_CONTACT_THRESHOLD = 10;

const canUseDisputeTab = (invoice?: Invoice): boolean => {
  if (!invoice) return false;

  return invoice.status !== InvoiceStatus.Approved;
};

const shouldShowDisputeContent = (invoice?: Invoice, disputeStatus?: string | null): boolean => {
  if (!invoice) return false;

  // Non-approved invoices can always use the dispute tab
  if (invoice.status !== InvoiceStatus.Approved) return true;

  return disputeStatus === 'resolved';
};

interface InvoiceTabContainerProps {
  invoice?: Invoice;
  tickets: Ticket[] | undefined;
  disputeAgent: ReturnType<typeof useDisputeAgent>;
}

export default function InvoiceTabContainer(props: InvoiceTabContainerProps) {
  if (!props.invoice) {
    // Hold tab initialization until the invoice is loaded so useTabs picks the
    // correct default on its first render — prevents the AI Review -> Overview flash.
    return <InvoiceTabsPlaceholder />;
  }
  return <InvoiceTabs {...props} invoice={props.invoice} />;
}

/**
 * Lightweight skeleton shown while the invoice is still loading. Mirrors the
 * loaded layout's outer footprint — a full-width tab strip at the real tab
 * height followed by a flex body — so the strip and top edge don't jump when
 * the real tabs mount. Deliberately no top-level padding (the real layout has
 * none) and minimal body content (just fills the space).
 */
function InvoiceTabsPlaceholder() {
  return (
    <Stack flex={1} minWidth={0}>
      <Skeleton variant="rectangular" width="100%" height={48} />
      <Box flex={1} sx={{ display: 'flex', p: 2 }}>
        <Stack spacing={2} flex={1}>
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="70%" />
        </Stack>
      </Box>
    </Stack>
  );
}

function InvoiceTabs({
  invoice,
  tickets,
  disputeAgent,
}: InvoiceTabContainerProps & { invoice: Invoice }) {
  const terms = useLocaleTerms();
  const counts = useRelatedCounts(invoice, tickets);
  const featureFlags = useFeatureFlags();
  const showDisputeManagement = featureFlags.disputeManagement === true;
  const flagsReady = useFlagsReady();

  const canDispute = canUseDisputeTab(invoice);
  const creditRecord = invoice ? isCredit(invoice) : false;
  const isDisputeReadOnly =
    invoice?.status === InvoiceStatus.Approved &&
    shouldShowDisputeContent(invoice, disputeAgent.state?.status);

  // No AI Review tab and no Overview tab: the review and what the invoice says both sit
  // in the header, visible whatever tab you're on, so every tab here is something the
  // invoice is attached to.
  // A credit has no tickets, cars, contract or dispute, only its notes and documents.
  const tabLabels = useMemo(
    () =>
      creditRecord
        ? [Tab.Notes, Tab.Attachments]
        : [
            ...RELATED_TABS,
            Tab.Notes,
            Tab.Attachments,
            ...(showDisputeManagement ? [Tab.Dispute] : []),
          ],
    [showDisputeManagement, creditRecord],
  );

  const defaultSelected: Tab = creditRecord ? Tab.Notes : RELATED_TABS[0];
  const { track } = useAnalytics();
  const { context, labelToTabId, selectTab } = useTabs(tabLabels, defaultSelected, {
    onTabChange: (tabLabel) => {
      if (invoice?.id) {
        track('invoice_tab_switched', { invoice_id: invoice.id, tab: tabLabel });
      }
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  // Deliberately not seeded with the current invoice. On the page the invoice is
  // already loaded by the time this container first renders, so a ref seeded with its
  // id matched on the very first run and the effect returned before it could apply the
  // tab the URL asked for — `?tab=dispute` landed on Linked Tickets every time.
  const prevInvoiceId = useRef<string | null>(null);

  // Selects the tab the URL asks for, and resets to the default when the viewed invoice
  // changes.
  //
  // The tab param is handled whether or not the invoice changed: a chat card or an email
  // link can point at the invoice already on screen, and nothing remounts on that
  // navigation, so a guard keyed only on the id would skip the tab.
  useEffect(() => {
    // `null` until the first run, so the first invoice counts as a change (which resets
    // the tab) without counting as the reader moving between invoices.
    const isFirstInvoice = prevInvoiceId.current === null;
    const invoiceChanged = invoice.id !== prevInvoiceId.current;
    const movedToAnotherInvoice = invoiceChanged && !isFirstInvoice;
    prevInvoiceId.current = invoice.id;

    // Matched case-insensitively against the labels: the tab values double as display
    // text ('dispute', 'notes'), and a link written by hand or built by an email
    // template carries whatever casing its author used.
    const requested = searchParams.get(selectedTabSearchParam);
    const matches = (label: string) => label.toLowerCase() === requested?.toLowerCase();
    const requestedTab = requested ? tabLabels.find(matches) : undefined;

    if (requestedTab) {
      selectTab(requestedTab);
    } else if (invoiceChanged) {
      // A tab the URL named but this invoice does not show is not a reason to move the
      // reader off the one they are on, so this only resets on a new invoice.
      selectTab(defaultSelected);
    }

    if (!requested) return;

    // Which tabs exist depends on flags, and `useFeatureFlags` reads `false` both for
    // "off" and "not delivered yet" — so on a cold load the Dispute tab is missing from
    // the first commit whatever the account is entitled to. Deleting the param there
    // turned a transient gap in flag delivery into permanent loss of the deep link.
    //
    // So the param is consumed only when it was acted on, or when waiting cannot help:
    // it names no tab this container has at all, or flags have settled and the reader
    // has moved on to another invoice, which bounds how long a value naming a tab this
    // account never sees can follow them around. `flagsReady` means "stop waiting"
    // rather than "LaunchDarkly answered", so a late delivery still applies for as long
    // as the reader stays on this invoice.
    const namesAKnownTab = Object.values(Tab).some(matches);
    const waitingCannotHelp = !namesAKnownTab || (flagsReady && movedToAnotherInvoice);

    if (requestedTab || waitingCannotHelp) {
      // Replaced rather than pushed, or Back returns to the URL that still carries it
      // and applies it again.
      setSearchParams(
        (prev) => {
          prev.delete(selectedTabSearchParam);
          return prev;
        },
        { replace: true },
      );
    }
  }, [
    invoice.id,
    searchParams,
    setSearchParams,
    selectTab,
    defaultSelected,
    tabLabels,
    flagsReady,
  ]);

  const isAdjustedInvoice = invoice.parentInvoice != null;
  const hostedInWorkQueueDialog = useHostedInWorkQueueDialog();

  const navigateToParentDispute = useCallback(() => {
    const parentInvoiceId = invoice.parentInvoice?.id;
    if (!parentInvoiceId) return;

    // Inside the Work Queue dialog the URL belongs to the queue behind it, and
    // `DetailParamRedirects` turns a `selectedInvoiceId` written there into a
    // navigation — which closes the dialog and takes the queue's filters, its scroll
    // position and the consultant's place in it along with it. The dialog already
    // sends plain links to a new tab, but that interception is on anchor clicks and
    // this is an `onClick`, so it has to do the same itself.
    if (hostedInWorkQueueDialog) {
      const search = new URLSearchParams(searchParams);
      search.delete(selectedInvoiceIdKey);
      search.set(selectedTabSearchParam, Tab.Dispute);
      window.open(`/spending/${parentInvoiceId}?${search.toString()}`, '_blank', 'noopener');
      return;
    }

    setSearchParams((prev) => {
      prev.set(selectedInvoiceIdKey, parentInvoiceId);
      prev.set(selectedTabSearchParam, Tab.Dispute);
      return prev;
    });
  }, [invoice, hostedInWorkQueueDialog, searchParams, setSearchParams]);

  const {
    userDetails: { id: userId },
  } = useContext(UserContext);

  const {
    addNoteToInvoice,
    invoiceNotes: { notes, loading: notesLoading },
  } = useInvoice(invoice?.id ?? null);

  const {
    state,
    timeline,
    loading,
    error,
    initiateDispute,
    generateDraft,
    updateDraft,
    discardDraft,
    sendEmail,
    resolve,
    withdraw,
    reopen,
    refreshState,
    refreshTimeline,
  } = disputeAgent;

  // Show badge when agent needs attention (active status or overdue awaiting vendor response)
  const needsAttention = useMemo(() => {
    if (!state) return false;
    if (state.status === 'active') return true;
    if (
      state.status === 'awaiting-vendor' &&
      state.daysSinceLastContact >= DAYS_SINCE_LAST_CONTACT_THRESHOLD
    )
      return true;
    return false;
  }, [state]);

  // The Dispute tab carries a persistent "Beta" chip while the Dispute Agent
  // rolls out to all consultants (mirrors the ContractIQ tab). The red
  // attention dot layers on top when the agent needs a look.
  const disputeTabLabel = useMemo(
    () => (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        {needsAttention ? (
          <Badge color="error" variant="dot" sx={{ '& .MuiBadge-badge': { top: 4, right: -6 } }}>
            Dispute
          </Badge>
        ) : (
          'Dispute'
        )}
        <Chip
          label="Beta"
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            height: 18,
            fontSize: '0.625rem',
            fontWeight: 600,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      </Box>
    ),
    [needsAttention],
  );

  const isSelected = (tab: Tab) => context.selectedTab === labelToTabId[tab];

  // Counts on the labels, so the strip is an index of what's attached before you open
  // anything. Each tab's chip counts what that tab holds — Cars Serviced counts the
  // elevators, not the tickets and invoices on its other two views.
  const overrideLabels = useMemo(() => {
    const labels: Record<string, ReactNode> = {};

    if (showDisputeManagement) labels[Tab.Dispute] = disputeTabLabel;

    labels[Tab.Tickets] = tabLabelWithCount('Linked Tickets', counts.linkedTickets);
    labels[Tab.RelatedInvoices] = tabLabelWithCount(
      'Linked Invoices & Proposals',
      counts.linkedInvoices,
    );
    labels[Tab.CarsServiced] = tabLabelWithCount(terms.vehiclesServiced, counts.carsServiced);
    labels[Tab.Contract] = tabLabelWithCount('Governing Contract', counts.contracts);
    labels[Tab.Notes] = tabLabelWithCount('Notes', notesLoading ? undefined : notes.length);
    // No number until the tickets are in, so it doesn't count up in steps once they arrive.
    labels[Tab.Attachments] = tabLabelWithCount(
      'Attachments',
      tickets === undefined ? undefined : countLinkedAttachments(invoice, tickets),
    );

    return labels;
  }, [
    showDisputeManagement,
    disputeTabLabel,
    terms.vehiclesServiced,
    counts,
    notes.length,
    notesLoading,
    invoice,
    tickets,
  ]);

  return (
    // `minWidth: 0` here and on the panel row: a flex item defaults to `min-width: auto`,
    // so a data grid wider than the window stretches every ancestor instead of scrolling
    // inside its own viewport.
    <Stack flex={1} minWidth={0}>
      <TabContext.Provider value={context}>
        <TabStrip
          overrideLabels={overrideLabels}
          styles={{
            tabs: {
              // Same surface as the header above it, running the full width of the band.
              backgroundColor: pageBandColor,
              px: { xs: 2, md: 3 },
              borderBottomLeftRadius: SURFACE_RADIUS,
              borderBottomRightRadius: SURFACE_RADIUS,
            },
          }}
        />
        <Box flex={1} minWidth={0} sx={{ display: 'flex' }}>
          {/* Bodies render only for the selected tab. Panels stay mounted while hidden, and
              each of these owns list requests. */}
          <TabPanel tabId={labelToTabId[Tab.Tickets]}>
            {isSelected(Tab.Tickets) ? (
              <RelatedPanel>
                <TicketsSection invoice={invoice} tickets={tickets} />
              </RelatedPanel>
            ) : null}
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.RelatedInvoices]}>
            {isSelected(Tab.RelatedInvoices) ? (
              <RelatedPanel>
                <InvoicesSection invoice={invoice} />
              </RelatedPanel>
            ) : null}
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.CarsServiced]}>
            {isSelected(Tab.CarsServiced) ? (
              <RelatedPanel>
                <CarsServicedSection invoice={invoice} />
              </RelatedPanel>
            ) : null}
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Contract]}>
            {isSelected(Tab.Contract) ? (
              <RelatedPanel>
                <ContractsSection invoice={invoice} />
              </RelatedPanel>
            ) : null}
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Notes]}>
            <Notes
              notes={notes}
              onAddNote={async (note) => {
                // Track only after the mutation resolves — failed
                // writes shouldn't be counted as "note added".
                await addNoteToInvoice(userId, note);
                if (invoice?.id) {
                  track('invoice_note_added', {
                    invoice_id: invoice.id,
                    note_length_bucket: bucketLength(note),
                  });
                }
              }}
            />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Attachments]}>
            <Attachments invoice={invoice} tickets={tickets} />
          </TabPanel>
          {showDisputeManagement && !creditRecord ? (
            <TabPanel tabId={labelToTabId[Tab.Dispute]}>
              {isAdjustedInvoice ? (
                <Box
                  sx={{
                    p: 3,
                    flexGrow: 1,
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <GavelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Adjusted Invoice
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}
                    >
                      This invoice is an adjustment of invoice #{invoice?.parentInvoice?.number}.
                      Disputes are managed on the original invoice.
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={navigateToParentDispute}
                      sx={{ ...BUTTON_SX, textTransform: 'none' }}
                    >
                      View Dispute on #{invoice?.parentInvoice?.number}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <DisputeAgentPanel
                  invoice={invoice}
                  canInitiateDispute={canDispute}
                  readOnly={isDisputeReadOnly}
                  agentState={state}
                  timeline={timeline}
                  loading={loading}
                  error={error}
                  onInitiateDispute={initiateDispute}
                  onGenerateDraft={generateDraft}
                  onUpdateDraft={updateDraft}
                  onDiscardDraft={discardDraft}
                  onSendEmail={sendEmail}
                  onResolve={resolve}
                  onWithdraw={withdraw}
                  onRefreshState={refreshState}
                  onReopen={reopen}
                  onRefreshTimeline={refreshTimeline}
                />
              )}
            </TabPanel>
          ) : null}
        </Box>
      </TabContext.Provider>
    </Stack>
  );
}
